import React from 'react';
import { format } from 'date-fns';
import { formatCurrency } from '@/lib/utils';
import { processDynamicFields } from '@/components/cost-center/document-defaults/dynamicFieldProcessor';
import { getCustomerDisplayName } from '@/utils/customerDisplayHelpers';
import '@/components/estimates/preview/pdf-styles.css';

interface CreditNoteDocumentProps {
  creditNote: {
    credit_note_number: string;
    credit_amount: number;
    description?: string | null;
    source_type: string;
    status: string;
    credit_note_issued_at: string;
    source_invoice?: {
      invoice_number?: string;
    } | null;
  };
  customer: {
    name: string;
    last_name: string;
    customer_type: string;
    business_name?: string | null;
    street_address?: string | null;
    suburb?: string | null;
    city?: string | null;
    province_state?: string | null;
    postal_code?: string | null;
    emails?: string[];
    cell_numbers?: string[];
  };
  company: {
    name: string;
    logo_url?: string | null;
    street_address?: string | null;
    suburb?: string | null;
    city?: string | null;
    province_state?: string | null;
    postal_code?: string | null;
    email?: string | null;
    company_phone_number?: string | null;
    vat_number?: string | null;
    'Account Holder'?: string | null;
    'Bank Name'?: string | null;
    'Account Number'?: string | null;
    'Branch Code'?: string | null;
    'Use Bank Info'?: boolean;
  };
  documentDefaults?: {
    credit_note_footer?: string | null;
  } | null;
  userProfile?: {
    first_name?: string | null;
    last_name?: string | null;
    email?: string | null;
    phone?: string | null;
  } | null;
  className?: string;
}

const CreditNoteDocument: React.FC<CreditNoteDocumentProps> = ({
  creditNote,
  customer,
  company,
  documentDefaults,
  userProfile,
  className = ''
}) => {

  const formatPhoneNumber = (phone: string | undefined): string => {
    if (!phone) return '';
    const cleaned = phone.replace(/\D/g, '');
    if (cleaned.length >= 10) {
      return `${cleaned.slice(0, 3)} ${cleaned.slice(3, 6)} ${cleaned.slice(6, 10)}`;
    }
    return phone;
  };

  const customerDisplayName = getCustomerDisplayName(customer);

  const getSourceDescription = () => {
    if (creditNote.source_type === 'overpayment' && creditNote.source_invoice?.invoice_number) {
      return `Overpayment on Invoice #${creditNote.source_invoice.invoice_number}`;
    }
    return creditNote.description || 'Credit issued';
  };

  const getStatusBadge = () => {
    const statusStyles: Record<string, { bg: string; text: string; label: string }> = {
      issued: { bg: '#dcfce7', text: '#166534', label: 'ISSUED' },
      applied: { bg: '#dbeafe', text: '#1e40af', label: 'APPLIED' },
      refunded: { bg: '#f3e8ff', text: '#7c3aed', label: 'REFUNDED' },
      cancelled: { bg: '#f3f4f6', text: '#6b7280', label: 'CANCELLED' },
    };
    
    const style = statusStyles[creditNote.status] || statusStyles.issued;
    return (
      <span 
        style={{ 
          backgroundColor: style.bg, 
          color: style.text,
          padding: '4px 12px',
          borderRadius: '4px',
          fontWeight: '600',
          fontSize: '12px'
        }}
      >
        {style.label}
      </span>
    );
  };

  const getFooterText = (): string => {
    if (documentDefaults?.credit_note_footer) {
      return processDynamicFields(documentDefaults.credit_note_footer, {
        user: {
          first_name: userProfile?.first_name,
          last_name: userProfile?.last_name,
          email: userProfile?.email,
          personal_phone_number: userProfile?.phone,
        },
        company: {
          name: company.name,
          email: company.email,
          company_phone_number: company.company_phone_number,
        },
        customer: {
          name: `${customer.name} ${customer.last_name}`,
          first_name: customer.name,
          last_name: customer.last_name,
          email: customer.emails?.[0],
          cell_numbers: customer.cell_numbers,
          street_address: customer.street_address,
          suburb: customer.suburb,
          city: customer.city,
          province_state: customer.province_state,
          postal_code: customer.postal_code,
        },
        system: {
          today_date: format(new Date(), 'dd MMMM yyyy'),
        },
      });
    }
    return '';
  };

  const renderPageHeader = () => (
    <div className="page-header-for-pdf" style={{ height: '120px', marginBottom: '20px', display: 'flex', alignItems: 'flex-start' }}>
      {company.logo_url && (
        <img 
          src={company.logo_url} 
          alt="Company Logo"
          className="page-logo-for-pdf"
          style={{ maxWidth: '400px', maxHeight: '150px', objectFit: 'contain' }}
        />
      )}
    </div>
  );

  const renderPageFooter = () => {
    const footerText = getFooterText();
    if (!footerText) return null;
    return (
      <div className="page-footer-for-pdf" style={{ 
        marginTop: '40px', 
        paddingTop: '20px', 
        borderTop: '1px solid #ccc',
        fontSize: '9px',
        fontStyle: 'italic',
        fontWeight: 'bold',
        color: 'black',
        lineHeight: '1.4',
        whiteSpace: 'pre-line'
      }}>
        <div>{footerText}</div>
      </div>
    );
  };

  return (
    <div className={`pdf-preview-wrapper ${className}`}>
      <div className="pdf-page-container">
        <div className="pdf-paper">
          <div className="pdf-margins">
            {/* Header */}
            {renderPageHeader()}

            {/* Main Content */}
            <div className="pdf-content" id="credit-note-preview" style={{ flex: 1 }}>
              {/* Header Section */}
              <div className="mb-8">
                {/* Credit Note Info - Top Right */}
                <div className="flex justify-end mb-6">
                  <div className="text-right">
                    <h2 className="text-xl font-bold text-gray-900">CREDIT NOTE</h2>
                    <div className="mt-2 mb-3">
                      {getStatusBadge()}
                    </div>
                    <div className="text-sm text-gray-600 mt-2">
                      <div><strong>Credit Note #:</strong> {creditNote.credit_note_number}</div>
                      <div>
                        <strong>Issue Date:</strong>{' '}
                        {format(new Date(creditNote.credit_note_issued_at), 'dd MMMM yyyy')}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Company Info - Left Column */}
                <div className="mb-6">
                  <h1 className="text-2xl font-bold text-gray-900">{company.name}</h1>
                  <div className="text-sm text-gray-600 mt-2">
                    {company.street_address && <div>{company.street_address}</div>}
                    {company.suburb && <div>{company.suburb}</div>}
                    {company.city && <div>{company.city}, {company.province_state} {company.postal_code}</div>}
                    {company.company_phone_number && (
                      <div><strong>Tel:</strong> {formatPhoneNumber(company.company_phone_number)}</div>
                    )}
                    {company.email && (
                      <div><strong>Email:</strong> {company.email}</div>
                    )}
                    {company.vat_number && (
                      <div><strong>VAT:</strong> {company.vat_number}</div>
                    )}
                  </div>
                </div>
              </div>

              {/* Customer Details */}
              <div className="mb-8">
                <h3 className="text-lg font-semibold mb-4">Credit Issued To:</h3>
                <div className="text-sm">
                  <div className="font-medium">{customerDisplayName}</div>
                  {customer.street_address && <div>{customer.street_address}</div>}
                  {customer.suburb && <div>{customer.suburb}</div>}
                  {customer.city && <div>{customer.city}, {customer.province_state} {customer.postal_code}</div>}
                  {customer.cell_numbers?.[0] && (
                    <div><strong>Phone:</strong> {formatPhoneNumber(customer.cell_numbers[0])}</div>
                  )}
                  {customer.emails?.[0] && (
                    <div><strong>Email:</strong> {customer.emails[0]}</div>
                  )}
                </div>
              </div>

              {/* Credit Details Table */}
              <div className="mb-8">
                <table className="w-full border-collapse border border-gray-300">
                  <thead>
                    <tr className="bg-gray-100">
                      <th className="border border-gray-300 px-4 py-2 text-left"><strong>Description</strong></th>
                      <th className="border border-gray-300 px-4 py-2 text-right"><strong>Amount</strong></th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td className="border border-gray-300 px-4 py-2">
                        {getSourceDescription()}
                      </td>
                      <td className="border border-gray-300 px-4 py-2 text-right">
                        {formatCurrency(creditNote.credit_amount)}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>

              {/* Total Credit Amount */}
              <div className="flex justify-end mb-8">
                <div style={{ 
                  backgroundColor: '#dcfce7', 
                  padding: '16px 24px', 
                  borderRadius: '6px',
                  textAlign: 'right'
                }}>
                  <div style={{ fontSize: '12px', color: '#166534', fontWeight: '500', marginBottom: '4px' }}>
                    Credit Amount
                  </div>
                  <div style={{ fontSize: '20px', fontWeight: '700', color: '#166534' }}>
                    {formatCurrency(creditNote.credit_amount)}
                  </div>
                </div>
              </div>

              {/* Bank Details for Refund (if applicable) */}
              {company['Use Bank Info'] && company['Account Number'] && creditNote.status === 'issued' && (
                <div className="mb-6 p-4 bg-gray-50 rounded">
                  <h4 className="text-sm font-semibold mb-2 text-gray-700">
                    Bank Details (for refund):
                  </h4>
                  <div className="text-sm text-gray-600">
                    <div><strong>Bank:</strong> {company['Bank Name']}</div>
                    <div><strong>Account Holder:</strong> {company['Account Holder']}</div>
                    <div><strong>Account Number:</strong> {company['Account Number']}</div>
                    <div><strong>Branch Code:</strong> {company['Branch Code']}</div>
                  </div>
                </div>
              )}
            </div>

            {/* Page Footer with dynamic content */}
            {renderPageFooter()}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CreditNoteDocument;
